import { http } from "utils/http";
import { CHANGELOG_MD_URL } from "./changelogTags";
import { parseChangelog, type ChangelogEntry } from "./parseChangelog";

/**
 * Plain module-level cache for the parsed changelog, exposed to React through
 * `useSyncExternalStore` (see `useChangelog`). Fetched once per session — either
 * eagerly right after login (see `prefetchChangelog`) or lazily the first time
 * the What's new panel opens — and shared by both the panel and the nav badge so
 * they stay in sync and never refetch. A failed fetch leaves `entries` null so a
 * later open can retry.
 *
 * This is deliberately not a MobX store: consumers subscribe via a hook and
 * re-render on change without needing to be `observer`s.
 */
export type ChangelogSnapshot = {
  entries: ChangelogEntry[] | null;
  loading: boolean;
  error: boolean;
};

let snapshot: ChangelogSnapshot = {
  entries: null,
  loading: false,
  error: false,
};

const listeners = new Set<() => void>();

// Replace the snapshot with a new object (so `useSyncExternalStore` sees an
// identity change) and notify subscribers.
const setSnapshot = (next: Partial<ChangelogSnapshot>): void => {
  snapshot = { ...snapshot, ...next };
  listeners.forEach((listener) => listener());
};

export const subscribeChangelog = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getChangelogSnapshot = (): ChangelogSnapshot => snapshot;

/** Fetch + parse once; a no-op while already loaded or in flight. */
export const loadChangelog = async (): Promise<void> => {
  if (snapshot.entries !== null || snapshot.loading) return;
  setSnapshot({ loading: true, error: false });

  try {
    const res = await http.fetch(CHANGELOG_MD_URL);
    const text = await res.text();
    setSnapshot({ entries: parseChangelog(text), loading: false });
  } catch {
    setSnapshot({ error: true, loading: false });
  }
};

/**
 * Prefetch the changelog so the panel opens instantly and the nav badge can
 * show an unseen count without waiting for the user to open the panel.
 * Fire-and-forget; safe to call more than once.
 */
export const prefetchChangelog = (): void => {
  void loadChangelog();
};
